import {
  type AccessorFn,
  type AccessorFnColumnDef,
  type AccessorKeyColumnDef,
  type Cell,
  type ColumnDef,
  createColumnHelper,
  type DeepKeys,
  type DeepValue,
  type DisplayColumnDef,
  type GroupColumnDef,
  type Header,
  type IdentifiedColumnDef,
  type RowData,
  type Table,
  type TableOptions,
  type TableState,
  type RowData as TanStackRowData,
} from "@tanstack/react-table"
import type * as React from "react"

export type OptionsWithoutDataAndColumns<TRowData extends TanStackRowData> = Omit<
  TableOptions<TRowData>,
  "data" | "columns"
>

export type DataTableElements = {
  bottomBar?: React.ReactNode
  bodySpinner?: React.ReactNode
  expandedRow?: React.ReactNode
}

export type DataTableConfig<_TRowData extends TanStackRowData> = {
  isExpandedRowSticky?: boolean
  shouldShowProgressIndicator?: boolean
}

/**
 * the plugin hook system lets us encapsulate custom logic and components
 * so that inside applications, we just need to put hooks together like legos
 * without worrying about the component-level implementation details
 */
export type UseDataTablePlugin<TRowData extends TanStackRowData> = {
  /**
   * these are all the options that can be passed to `useTanstackTable` hook
   */
  options?: Partial<OptionsWithoutDataAndColumns<TRowData>>
  /**
   * this is `state` argument of `useTanstackTable` hook
   */
  state?: Partial<TableState>
  /**
   * Slots are table-level components that can be customised.
   * They can use the `useDataTableCtx` hook to access the table state.
   */
  elements?: DataTableElements
  /**
   * Config is table-level configuration that can be customised
   */
  config?: DataTableConfig<TRowData>
  /**
   * This is a function that can be used to get the props for a cell.
   * It is used to apply the plugin's custom props to the cell.
   */
  getCellProps?: (
    cell: Cell<TRowData, unknown>,
    scrollState: DataTableScrollState
  ) => {
    className?: string
    style?: React.CSSProperties
  }
  /**
   * This is a function that can be used to get the props for a header.
   * It is used to apply the plugin's custom props to the header.
   */
  getHeaderProps?: (
    cell: Header<TRowData, unknown>,
    scrollState: DataTableScrollState
  ) => {
    className?: string
    style?: React.CSSProperties
  }
}

export type DataTableHookReturn<TRowData extends TanStackRowData> = Partial<
  TableOptions<TRowData>
> & {
  /**
   * this is the return value of `useTanstackTable` hook
   */
  table: Table<TRowData>
  /**
   * this is `columns` argument of `useTanstackTable` hook
   */
  columns: ColumnDef<TRowData>[]
  /**
   * Slots are table-level components that can be customised.
   * They can use the `useDataTableCtx` hook to access the table state.
   */
  elements: NonNullable<UseDataTablePlugin<TRowData>["elements"]>
  /**
   * Config is table-level configuration that can be customised
   */
  config: NonNullable<UseDataTablePlugin<TRowData>["config"]>

  /**
   * The plugins that have been applied to your current DataTable instance.
   * We expose this so that your custom components can manipulate things like expansion state, selection state, etc.
   */
  appliedPlugins: UseDataTablePlugin<TRowData>[]
}

export type DataTableColumnHelper<TData extends RowData, TColumnIds extends string> = {
  accessor: <
    TAccessor extends AccessorFn<TData> | DeepKeys<TData>,
    TValue extends TAccessor extends AccessorFn<TData, infer TReturn>
      ? TReturn
      : TAccessor extends DeepKeys<TData>
        ? DeepValue<TData, TAccessor>
        : never,
  >(
    accessor: TAccessor,
    column: TAccessor extends AccessorFn<TData>
      ? DisplayColumnDef<TData, TValue> & { id: TColumnIds }
      : IdentifiedColumnDef<TData, TValue> & { id: TColumnIds }
  ) => TAccessor extends AccessorFn<TData>
    ? AccessorFnColumnDef<TData, TValue>
    : AccessorKeyColumnDef<TData, TValue>
  display: (
    column: DisplayColumnDef<TData> & { id: TColumnIds }
  ) => DisplayColumnDef<TData, unknown>
  group: (column: GroupColumnDef<TData> & { id: TColumnIds }) => GroupColumnDef<TData, unknown>
}

/**
 * Simple facade over TanStack's createColumnHelper that enforces id fields
 *
 * Usage:
 * ```ts
 * const columnHelper = dtColumnHelper<Payment>();
 *
 * const columns = [
 *   columnHelper.accessor("email", {
 *     id: "email", // Required by our custom type
 *     header: "Email",
 *   }),
 *   columnHelper.display({
 *     id: "actions", // Required by our custom type
 *     cell: () => <Button>Action</Button>
 *   })
 * ];
 * ```
 */
export function dtColumnHelper<TData extends RowData, TColumnIds extends string>(): [
  /**
   * The column helper
   */
  DataTableColumnHelper<TData, TColumnIds>,
  /**
   * A function that can be exported from your table so that other components
   * can get access to strictly typed column ids without having to declare enums etc
   *
   */
  (id: TColumnIds) => TColumnIds,
] {
  return [
    // column helper
    createColumnHelper<TData>() as DataTableColumnHelper<TData, TColumnIds>,

    <ActualId extends TColumnIds>(id: TColumnIds) => id as ActualId,
  ]
}

export type DataTableScrollState = {
  isHorizontalScroll: boolean
  isVerticalScroll: boolean
  scrollHeight: number
  scrollLeft: number
  scrollTop: number
  scrollWidth: number
}
